import React from 'react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { Input } from '@appkit4/react-components/field';
import { Button } from '@appkit4/react-components/button';
import { Tabs, Tab } from '@appkit4/react-components/tabs';
import { Select } from '@appkit4/react-components/select';
import {
    UserResponse,
    RoleResponse,
} from '../../../../../api/generated/models';
import useLocale from '../../../../../locale/useLocale';
import Roles from '../../../../../auth/roles';

interface TenantOption {
    label: string;
    value: number;
}

interface EditUserFormProps {
    roles: RoleResponse[];
    user: UserResponse;
    currentTenantId: number;
    canEditTenant: boolean;
    tenantOptions: TenantOption[];
    submitFunc: (values: EditUserFormValues) => void;
}

export interface EditUserFormValues {
    email: string;
    forenames: string;
    roleId: number;
    surname: string;
    tenantId: number;
}

const isValidSuperAdminEmail = (email?: string) => {
    const normalisedEmail = email?.trim().toLowerCase() ?? '';

    return (
        normalisedEmail.endsWith('@admin.pwc.com') ||
        normalisedEmail.endsWith('@pwc.com')
    );
};

const EditUserForm = (props: EditUserFormProps) => {
    const { l } = useLocale();

    const EditUserSchema = Yup.object({
        email: Yup.string()
            .email(l('_pleaseEnterAValidEmailAddress'))
            .max(250, l('_pleaseEnterFewerCharacters'))
            .required(l('_pleaseEnterAnEmailAddress'))
            .test(
                'superadminEmailCheck',
                l('_superAdminEmailMustEndWith'),
                function (value: string): boolean {
                    const roleId = this.parent.roleId;

                    if (
                        roleId === Roles.SuperAdminId &&
                        !isValidSuperAdminEmail(value)
                    ) {
                        return false;
                    }

                    return true;
                }
            ),
        forenames: Yup.string()
            .trim()
            .max(100, l('_pleaseEnterFewerCharacters'))
            .required(l('_pleaseEnterForename(s)')),
        roleId: Yup.number()
            .min(Roles.UserId, l('_pleaseSelectAValidRole'))
            .max(Roles.SuperAdminId, l('_pleaseSelectAValidRole'))
            .required(l('_pleaseSelectARole')),
        surname: Yup.string()
            .trim()
            .max(100, l('_pleaseEnterFewerCharacters'))
            .required(l('_pleaseEnterASurname')),
        tenantId: Yup.number()
            .min(1, l('_userFormInvalidTenant'))
            .required(l('_userFormMissingTenant')),
    });

    return (
        <Formik
            enableReinitialize
            initialValues={{
                email: props.user.email,
                forenames: props.user.forenames,
                roleId:
                    props.roles.find((r) => r.name === props.user.roleName)
                        ?.id || -1,
                surname: props.user.surname,
                tenantId: props.currentTenantId,
            }}
            validationSchema={EditUserSchema}
            onSubmit={(values) => props.submitFunc(values)}
        >
            {({
                values,
                errors,
                touched,
                setFieldTouched,
                setFieldValue,
                validateField,
            }) => (
                <Form>
                    <div className="row">
                        <div className="col-auto">
                            <div className="row-auto">
                                <Tabs
                                    type="filled"
                                    onTabChange={(i: number, value: string) => {
                                        setFieldTouched('roleId');
                                        validateField('roleId');
                                        setFieldValue('roleId', +value);
                                    }}
                                    aria-label={l('_editUserRole')}
                                    activeIndex={props.roles.findIndex(
                                        (r) => r.id == values.roleId
                                    )}
                                >
                                    {props.roles.map((r) => (
                                        <Tab
                                            className={`roleId${r.id}Tab`}
                                            label={r.name}
                                            value={r.id.toString()}
                                            key={r.id}
                                        />
                                    ))}
                                </Tabs>
                                {touched.roleId && errors.roleId && (
                                    <div className="error-text">
                                        {errors.roleId}
                                    </div>
                                )}
                            </div>
                            {props.roles.some(
                                (r) => r.id == Roles.SuperAdminId
                            ) &&
                                values.roleId === Roles.SuperAdminId && (
                                    <div className="row">
                                        <p className="ap-typography-body-xs ap-text-neutral-10">
                                            {l('_superAdminEmailRequirement')}
                                        </p>
                                    </div>
                                )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md">
                            <Input
                                type="text"
                                title={l('_forename(s)')}
                                placeholder={l('_forename(s)')}
                                required
                                value={values.forenames}
                                onChange={(value: string) => {
                                    setFieldTouched('forenames');
                                    validateField('forenames');
                                    setFieldValue('forenames', value);
                                }}
                                className="mt-3"
                                data-testid="EditForenameTestId"
                            />
                            {touched.forenames && errors.forenames && (
                                <div className="error-text">
                                    {errors.forenames}
                                </div>
                            )}
                        </div>
                        <div className="col-md">
                            <Input
                                type="text"
                                title={l('_surname')}
                                placeholder={l('_surname')}
                                required
                                value={values.surname}
                                onChange={(value: string) => {
                                    setFieldTouched('surname');
                                    validateField('surname');
                                    setFieldValue('surname', value);
                                }}
                                className="mt-3"
                                data-testid="EditSurnameTestId"
                            />
                            {touched.surname && errors.surname && (
                                <div className="error-text">
                                    {errors.surname}
                                </div>
                            )}
                        </div>
                    </div>
                    <Input
                        type="text"
                        title={l('_email')}
                        placeholder={l('_email')}
                        required
                        value={values.email}
                        onChange={(value: string) => {
                            setFieldTouched('email');
                            validateField('email');
                            setFieldValue('email', value);
                        }}
                        className="mt-3"
                        data-testid="EditEmailTestId"
                    />
                    {touched.email && errors.email && (
                        <div className="error-text">{errors.email}</div>
                    )}
                    {props.canEditTenant && (
                        <div className="mt-3">
                            <label className="ap-typography-body-s d-block mb-2">
                                {l('_tenant')}
                            </label>
                            <Select
                                data={props.tenantOptions}
                                searchable={false}
                                defaultValue={values.tenantId}
                                placeholder={l('_tenant')}
                                onSelect={(selected) => {
                                    setFieldTouched('tenantId');
                                    validateField('tenantId');
                                    setFieldValue('tenantId', Number(selected));
                                }}
                                data-testid="EditTenantTestId"
                            />
                            {touched.tenantId && errors.tenantId && (
                                <div className="error-text">
                                    {errors.tenantId}
                                </div>
                            )}
                        </div>
                    )}
                    <Button className="ms-auto mt-5" type="submit">
                        {l('_updateUser')}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default EditUserForm;
